import React, { useState, useRef, useEffect, useMemo } from "react";

const SearchRow = ({
    allItems = [],
    onSelectItem,
    selectedItemId,
    label,
    disabled = false,
}) => {
    const [searchByIdValue, setSearchByIdValue] = useState("");
    const [searchByNameValue, setSearchByNameValue] = useState("");
    const [searchByCategoryValue, setSearchByCategoryValue] = useState("");
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isIdDropdownOpen, setIsIdDropdownOpen] = useState(false);
    const [isNameDropdownOpen, setIsNameDropdownOpen] = useState(false);
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [selectedDescription, setSelectedDescription] = useState("");

    const idDropdownRef = useRef(null);
    const nameDropdownRef = useRef(null);
    const categoryDropdownRef = useRef(null);
    const idInputRef = useRef(null);
    const nameInputRef = useRef(null);
    const categoryInputRef = useRef(null);

    const categories = useMemo(() => {
        const categoryMap = new Map();
        allItems
            .filter((item) => item?.CategoryName && item?.CategoryID)
            .forEach((item) => {
                if (!categoryMap.has(item.CategoryID)) {
                    categoryMap.set(item.CategoryID, {
                        CategoryName: item.CategoryName,
                        CategoryID: item.CategoryID,
                        id: item.CategoryID,
                        name: item.CategoryName,
                    });
                }
            });
        return Array.from(categoryMap.values());
    }, [allItems]);

    const filteredItems = useMemo(() => {
        if (!selectedCategory) return allItems;
        return allItems.filter(
            (item) => item?.CategoryID === selectedCategory.CategoryID
        );
    }, [allItems, selectedCategory]);

    const filteredById = useMemo(() => {
        return filteredItems
            .filter((item) => {
                if (!item || !item.ItemID) return false;
                return searchByIdValue
                    ? item.ItemID.toString().toLowerCase().includes(searchByIdValue.toLowerCase())
                    : true;
            })
            .slice(0, 10);
    }, [filteredItems, searchByIdValue]);

    const filteredByName = useMemo(() => {
        return filteredItems
            .filter((item) => {
                if (!item || !item.ItemName) return false;
                return searchByNameValue
                    ? item.ItemName.toLowerCase().includes(searchByNameValue.toLowerCase())
                    : true;
            })
            .slice(0, 10);
    }, [filteredItems, searchByNameValue]);

    const filteredCategories = useMemo(() => {
        return categories.filter((category) => {
            if (!searchByCategoryValue) return true;
            return (
                category.CategoryName.toLowerCase().includes(searchByCategoryValue.toLowerCase()) ||
                category.CategoryID.toString().toLowerCase().includes(searchByCategoryValue.toLowerCase())
            );
        });
    }, [categories, searchByCategoryValue]);

    const handleItemSelect = (item) => {
        if (!item) return;
        onSelectItem(item);
        setSearchByIdValue(item.ItemID.toString());
        setSearchByNameValue(item.ItemName);
        setSelectedDescription(item.DESCR || "No description available");
        setIsIdDropdownOpen(false);
        setIsNameDropdownOpen(false);
        setSelectedIndex(-1);

        if (item.CategoryID) {
            const category = categories.find((c) => c.CategoryID === item.CategoryID);
            if (category) {
                setSelectedCategory(category);
                setSearchByCategoryValue(category.CategoryName);
            }
        }
    };

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        setSearchByCategoryValue(category.CategoryName);
        setIsCategoryDropdownOpen(false);
        setSelectedIndex(-1);
    };

    const handleKeyDown = (e, items, isOpen, onSelect, setDropdownOpen) => {
        if (!isOpen || !items.length) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev) => 
                    prev < items.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev) => 
                    prev > 0 ? prev - 1 : 0
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < items.length) {
                    onSelect(items[selectedIndex]);
                    setDropdownOpen(false);
                    setSelectedIndex(-1);
                }
                break;
            case 'Escape':
                setDropdownOpen(false);
                setSelectedIndex(-1);
                break;
            default:
                break;
        }
    };

    const renderDropdown = (items, isOpen, dropdownRef, onSelect, type = "item", position = "left") => {
        if (!isOpen || items.length === 0 || disabled) return null;

        const dropdownStyles = {
            width: 'max-content',
            maxWidth: type === "category" ? '400px' : '500px',
            minWidth: '100%', // This ensures dropdown is at least as wide as the input
        };

        if (position === "right") {
            dropdownStyles.right = '0';
        } else {
            dropdownStyles.left = '0';
        }

        return (
            <div
                ref={dropdownRef}
                className="absolute z-50 max-h-60 overflow-auto bg-white border rounded-b shadow-lg mt-1"
                style={dropdownStyles}
            >
                {items.map((item, index) => (
                    <div
                        key={type === "category" ? item.CategoryID : item.ItemID}
                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                            index === selectedIndex ? "bg-blue-50" : ""
                        }`}
                        onClick={() => onSelect(item)}
                    >
                        <div className="flex items-center gap-4 whitespace-nowrap overflow-hidden">
                            <span className="font-medium min-w-[150px]">
                                {type === "category" ? item.CategoryName : item.ItemName}
                            </span>
                            <span className="text-gray-500 shrink-0">
                                ID: {type === "category" ? item.CategoryID : item.ItemID}
                            </span>
                            {type !== "category" && item.DESCR && (
                                <span className="text-gray-600 truncate">
                                    {item.DESCR}
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const handleInputFocus = (setDropdownOpen) => {
        if (!disabled) {
            setDropdownOpen(true);
            setSelectedIndex(-1);
        }
    };

    const renderInput = (
        value,
        onChange,
        placeholder,
        inputRef,
        setDropdownOpen,
        handleClear,
        items,
        isOpen,
        onSelect
    ) => (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                className="w-full px-3 py-2 border rounded"
                value={value}
                onChange={onChange}
                onFocus={() => handleInputFocus(setDropdownOpen)}
                onKeyDown={(e) => handleKeyDown(e, items, isOpen, onSelect, setDropdownOpen)}
                placeholder={placeholder}
                disabled={disabled}
            />
            {value && (
                <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={handleClear}
                    type="button"
                >
                    ×
                </button>
            )}
        </div>
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                !idDropdownRef.current?.contains(event.target) &&
                !idInputRef.current?.contains(event.target)
            ) {
                setIsIdDropdownOpen(false);
            }
            if (
                !nameDropdownRef.current?.contains(event.target) &&
                !nameInputRef.current?.contains(event.target)
            ) {
                setIsNameDropdownOpen(false);
            }
            if (
                !categoryDropdownRef.current?.contains(event.target) &&
                !categoryInputRef.current?.contains(event.target)
            ) {
                setIsCategoryDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-12 gap-4">
                {/* Category Selection */}
                <div className="col-span-4 relative">
                    <label className="block text-sm font-medium mb-1">Category</label>
                    {renderInput(
                        searchByCategoryValue,
                        (e) => setSearchByCategoryValue(e.target.value),
                        "Search category...",
                        categoryInputRef,
                        setIsCategoryDropdownOpen,
                        () => {
                            setSearchByCategoryValue("");
                            setSelectedCategory(null);
                        },
                        filteredCategories,
                        isCategoryDropdownOpen,
                        handleCategorySelect
                    )}
                    {renderDropdown(
                        filteredCategories,
                        isCategoryDropdownOpen,
                        categoryDropdownRef,
                        handleCategorySelect,
                        "category",
                        "left"
                    )}
                </div>

                {/* Item ID Selection */}
                <div className="col-span-2 relative">
                    <label className="block text-sm font-medium mb-1">Item ID</label>
                    {renderInput(
                        searchByIdValue,
                        (e) => setSearchByIdValue(e.target.value),
                        "Search ID...",
                        idInputRef,
                        setIsIdDropdownOpen,
                        () => {
                            setSearchByIdValue("");
                            onSelectItem(null);
                        },
                        filteredById,
                        isIdDropdownOpen,
                        handleItemSelect
                    )}
                    {renderDropdown(
                        filteredById,
                        isIdDropdownOpen,
                        idDropdownRef,
                        handleItemSelect,
                        "item",
                        "left"
                    )}
                </div>

                {/* Item Name Selection */}
                <div className="col-span-6 relative">
                    <label className="block text-sm font-medium mb-1">Item Name</label>
                    {renderInput(
                        searchByNameValue,
                        (e) => setSearchByNameValue(e.target.value),
                        "Search name...",
                        nameInputRef,
                        setIsNameDropdownOpen,
                        () => {
                            setSearchByNameValue("");
                            onSelectItem(null);
                        },
                        filteredByName,
                        isNameDropdownOpen,
                        handleItemSelect
                    )}
                    {renderDropdown(
                        filteredByName,
                        isNameDropdownOpen,
                        nameDropdownRef,
                        handleItemSelect,
                        "item",
                        "right"
                    )}
                </div>
            </div>

            {/* Description Display */}
            {selectedDescription && (
                <div className="mt-4">
                    <label className="block text-sm font-medium mb-1">Item Description</label>
                    <div className={`border rounded p-3 ${selectedDescription !== "No description available" ? "bg-yellow-100" : "bg-gray-50"}`}>
                        <p className="text-gray-700">{selectedDescription}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchRow;